import { boardRow } from "../neucleos/boardRegistryClient";
import { useLaunchableWorkrooms } from "../neucleos/workroomLaunch";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";

import { resolveAddressSessionId } from "../neucleos/agentRailAddress";
import type { ModelSelection } from "@cleo/contracts";
import { seatGovernedSessionBirth } from "../neucleos/worldViewController";
import { consumePendingGovernedBirth } from "../neucleos/worldBirthChooser";
import { useNeucleosWorldStore } from "../neucleos/worldStore";
import { useWorldView } from "../neucleos/worldView";
import { Dialog, DialogPopup } from "./ui/dialog";
import { NewSessionBirthForm, useNewSessionBirth } from "./WorldSessionStrip";

/**
 * The one seat behind openGovernedSessionBirth: mounted under
 * WorldViewProvider, it subscribes only while the fronted World's room
 * is ready and opens the joined Session + Agent ceremony as a dialog. The birth
 * parents to the ADDRESSED Session — the route leaf, overridden by a valid
 * `?a=` address (RAIL-5b). An Agent-rail control supplies its exact displayed
 * Session explicitly, so a folded/fronted rail cannot be reinterpreted through
 * whatever route happens to be active.
 */
export function GovernedSessionBirthHost(): ReactElement | null {
  const { state } = useWorldView();
  const routeSearch = useLocation({ select: (location) => location.search }) as Record<
    string,
    unknown
  >;
  const requestedAddress =
    typeof routeSearch["a"] === "string" && routeSearch["a"].length > 0
      ? (routeSearch["a"] as string)
      : undefined;
  const routeParentSessionId =
    state.kind === "world-ready"
      ? resolveAddressSessionId(state.index, state.activeSessionId, requestedAddress)
      : null;
  const [requestedBirth, setRequestedBirth] = useState<{
    readonly birthSource: "world" | "parent";
    /** A session id for a parent birth; a ROOM id (R_) for a world birth into a named room; null = the active room. */
    readonly parentId: string | null;
    readonly workroomKey?: string;
  } | null>(null);
  const parentId = requestedBirth?.parentId ?? routeParentSessionId;
  const launchableRows = useLaunchableWorkrooms(
    state.kind === "world-ready" ? state.worldKey : null,
  );
  const [open, setOpen] = useState(false);
  const [pendingBoardLaunch, setPendingBoardLaunch] = useState<string | null>(null);
  // The picker's D1 door: the request names the provider and model the
  // ceremony opens with; the person still confirms the birth.
  const [requestedModel, setRequestedModel] = useState<ModelSelection | null>(null);
  const birth = useNewSessionBirth({
    birthSource: requestedBirth?.birthSource ?? "parent",
    onOpened: () => {
      setOpen(false);
      setRequestedBirth(null);
    },
    parentId,
    workroomKey: requestedBirth?.workroomKey ?? null,
  });
  const board =
    requestedBirth?.workroomKey === undefined
      ? null
      : {
          workroomKey: requestedBirth.workroomKey,
          title:
            launchableRows.find((row) => row.key === requestedBirth.workroomKey)?.title ??
            (state.kind === "world-ready"
              ? boardRow(state.worldKey, requestedBirth.workroomKey)?.title
              : undefined) ??
            requestedBirth.workroomKey,
        };
  const { launchBoard } = birth;
  useEffect(() => {
    if (pendingBoardLaunch === null) return;
    setPendingBoardLaunch(null);
    void launchBoard(pendingBoardLaunch);
  }, [launchBoard, pendingBoardLaunch]);
  const boardNeedsThePerson =
    board !== null && (birth.error !== null || birth.partialSessionId !== null);
  useEffect(() => {
    if (boardNeedsThePerson) setOpen(true);
  }, [boardNeedsThePerson]);
  const ready = state.kind === "world-ready";
  const activeWorldKey = useNeucleosWorldStore((worldState) => worldState.activeWorldKey);
  // The ALL stance's answer arrives here: a World chosen in the birth
  // chooser carried a pending birth across the root navigation, and the
  // moment this World's room seats, the ceremony it owes opens — anchored to
  // the World, exactly as if the + had been pressed in the room itself.
  useEffect(() => {
    if (!ready) return;
    if (!consumePendingGovernedBirth(activeWorldKey)) return;
    setRequestedBirth({ birthSource: "world", parentId: null });
    setOpen(true);
  }, [activeWorldKey, ready]);
  useEffect(() => {
    if (!ready || state.kind !== "world-ready") return undefined;
    return seatGovernedSessionBirth((request) => {
      setRequestedModel(request.modelSelection ?? null);
      if (request.birthSource === "world") {
        if (request.workroomKey !== undefined) {
          setRequestedBirth({
            birthSource: "world",
            parentId: request.roomId ?? null,
            workroomKey: request.workroomKey,
          });
          setPendingBoardLaunch(request.workroomKey);
          return;
        }
        setRequestedBirth({ birthSource: "world", parentId: request.roomId ?? null });
        setOpen(true);
        return;
      }
      if (request.parentId === undefined) {
        setRequestedBirth(null);
        setOpen(true);
        return;
      }
      const exactParent = state.index.sessions.some((slice) => slice.sessionId === request.parentId)
        ? request.parentId
        : routeParentSessionId;
      if (exactParent === null) return;
      setRequestedBirth({
        birthSource: request.birthSource ?? "parent",
        parentId: exactParent,
      });
      setOpen(true);
    });
  }, [ready, routeParentSessionId, state]);
  const seatRequestedModel = birth.setModelSelection;
  useEffect(() => {
    if (requestedModel) seatRequestedModel(requestedModel);
  }, [requestedModel, seatRequestedModel]);
  if (!ready) return null;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next && birth.partialSessionId === null) {
          birth.clearError();
          setRequestedBirth(null);
        }
      }}
    >
      <DialogPopup className="w-80">
        <div
          data-governed-session-birth={requestedBirth?.birthSource === "world" ? "world" : parentId}
        >
          <NewSessionBirthForm
            birth={birth}
            board={board}
            headline="New general session"
            launchLabel={
              requestedBirth?.birthSource === "world"
                ? "Start new general session from World"
                : "Start nested session from parent"
            }
          />
        </div>
      </DialogPopup>
    </Dialog>
  );
}
